import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { applicationAPI } from '../api/services';

interface Application {
  _id: string;
  name: string;
  description: string;
  applicationType: 'backend' | 'frontend_web' | 'mobile_ios' | 'mobile_android' | 'desktop' | 'service' | 'library' | 'other';
  productIds: string[];
  owner: {
    userId: string;
    name: string;
    email: string;
  };
  departmentId: string;
  departmentName: string;
  visibility: 'public' | 'private';
  status: string;
  tags: string[];
  techStack: string[];
  version?: string;
  stats: {
    totalProjects: number;
    totalBugs: number;
    activeBugs: number;
    totalTasks: number;
    completedTasks: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface ApplicationState {
  applications: Application[];
  currentApplication: Application | null;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filters: {
    applicationType?: string;
    status?: string;
    productId?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const initialState: ApplicationState = {
  applications: [],
  currentApplication: null,
  loading: false,
  error: null,
  searchQuery: '',
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const fetchApplications = createAsyncThunk(
  'applications/fetchApplications',
  async (params: {
    page?: number;
    limit?: number;
    applicationType?: string;
    status?: string;
    productId?: string;
    visibility?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const response = await applicationAPI.getAllApplications(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch applications');
    }
  }
);

export const fetchApplicationById = createAsyncThunk(
  'applications/fetchApplicationById',
  async ({ id, includeProjects }: { id: string; includeProjects?: boolean }, { rejectWithValue }) => {
    try {
      const response = await applicationAPI.getApplicationById(id, includeProjects);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch application');
    }
  }
);

export const fetchApplicationsByProduct = createAsyncThunk(
  'applications/fetchApplicationsByProduct',
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await applicationAPI.getApplicationsByProduct(productId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch applications');
    }
  }
);

export const createApplication = createAsyncThunk(
  'applications/createApplication',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await applicationAPI.createApplication(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create application');
    }
  }
);

export const updateApplication = createAsyncThunk(
  'applications/updateApplication',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await applicationAPI.updateApplication(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update application');
    }
  }
);

export const deleteApplication = createAsyncThunk(
  'applications/deleteApplication',
  async (id: string, { rejectWithValue }) => {
    try {
      await applicationAPI.deleteApplication(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete application');
    }
  }
);

const applicationSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<ApplicationState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchQuery = '';
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentApplication: (state) => {
      state.currentApplication = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch applications
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch application by ID
      .addCase(fetchApplicationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentApplication = action.payload.data.application || action.payload.data;
      })
      .addCase(fetchApplicationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch applications by product
      .addCase(fetchApplicationsByProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationsByProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload.data;
      })
      .addCase(fetchApplicationsByProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create application
      .addCase(createApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.applications.unshift(action.payload.data);
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update application
      .addCase(updateApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.applications.findIndex(a => a._id === action.payload.data._id);
        if (index !== -1) {
          state.applications[index] = action.payload.data;
        }
        if (state.currentApplication?._id === action.payload.data._id) {
          state.currentApplication = action.payload.data;
        }
      })
      .addCase(updateApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete application
      .addCase(deleteApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = state.applications.filter(a => a._id !== action.payload);
      })
      .addCase(deleteApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchQuery,
  setFilters,
  clearFilters,
  setPage,
  clearError,
  clearCurrentApplication,
} = applicationSlice.actions;

export default applicationSlice.reducer;
